import React, { useState } from 'react';
import { getAuth, setAuth } from '../../contexts/authStore';
import {
  Box,
  Container,
  Grid,
  Paper,
  Typography,
  Avatar,
  IconButton,
  Chip,
  ListItemText,
  ListItemIcon,
  Tab,
  Tabs,
  Menu,
  MenuItem} from '@mui/material';
import {
  Person as PersonIcon,
  PhotoCamera as PhotoCameraIcon,
  Notifications as NotificationsIcon,
  Security as SecurityIcon,
  CreditCard as CreditCardIcon,
  Cancel as CancelIcon} from '@mui/icons-material';
import { useNavigate } from 'react-router-dom';
import moment from 'moment';
import { getUser, getUserNotificationSettings, putUser, putUserNotificationSettings } from '../../lib/connector/userQueries';
import { getUserPool } from '../../lib/pools/pools';
import { useToast } from '../../components/modals/ToastProvider/ToastProvider';
import { changePassword, logout, updateUserAttribute, verifyUserAttribute } from '../../lib/authenticate';
import { cancelSubscription, deletePaymentMethod, getCustomer, getTransactions, getUserPaymentMethod, putSubscription, updateSubscription } from '../../lib/connector/paypalQueries';
import { Transaction } from '../../components/BillingHistory';

// Import Tab Components
import ProfileTab from '../../components/ProfileTab';
import NotificationsTab from '../../components/NotificationsTab';
import SecurityTab from '../../components/SecurityTab';
import BillingTab from '../../components/BillingTab';

// Import Modal Components
import AddPaymentModal from '../../components/modals/AddPaymentModal';
import ManagePaymentMethodModal from '../../components/modals/ManagePaymentMethodModal';
import DeleteAccountModal from '../../components/modals/DeleteAccountModal';
import SmsConsentModal from '../../components/modals/SmsConsentModal';
import PlanSwitchModal from '../../components/modals/PlanSwitchModal';
import CancelSubscriptionModal from '../../components/modals/CancelSubscriptionModal';
import MainHeader from '../../components/MainHeader';
import { getSchedules } from '../../lib/connector/scheduleQueries';
import { PaymentMethod } from '../../components/modals/ManagePaymentMethodModal/ManagePaymentMethodModal';
import { putNotificationRequest } from '../../lib/connector/requestQueries';

// Plan configuration
const SUBSCRIPTION_PLANS = [
  {
    id: 'free',
    name: 'Free',
    price: 0,
    period: 'month',
    description: 'Free plan for individuals getting started',
    features: [
      '1 schedule',
      'Up to 5 staff members',
      'Basic features'
    ],
    maxSchedules: 1,
    maxStaff: 5,
    planId: null
  },
  {
    id: 'basic',
    name: 'Basic',
    price: 10,
    period: 'month',
    description: 'Ideal for small teams',
    features: [
      '1 schedule',
      'Up to 5 staff members',
      'Basic features'
    ],
    maxSchedules: 1,
    maxStaff: 5,
    planId: 'v79p'
  },
  {
    id: 'pro',
    name: 'Pro',
    price: 30,
    period: 'month',
    description: 'Ideal for growing businesses',
    features: [
      '10 schedules',
      'Up to 15 staff members',
      'Schedule templates',
      'Basic analytics'
    ],
    maxSchedules: 10,
    maxStaff: 15,
    planId: '2fwt',
    badge: 'Most Popular',
    highlighted: true
  },
  {
    id: 'business',
    name: 'Business',
    price: 80,
    period: 'month',
    description: 'Ideal for established businesses',
    features: [
      'Unlimited schedules',
      'Up to 35 staff members',
      'Schedule templates',
      'Advanced analytics',
      'Priority support'
    ],
    maxSchedules: -1, // unlimited
    maxStaff: 35,
    planId: 'cdpj'
  }
];

export default function ProfileSettings() {
  const toast = useToast();
  const navigate = useNavigate();

  // Modal state management
  const [showDeleteModal, setShowDeleteModal] = useState(false);
  const [showSmsConsentModal, setShowSmsConsentModal] = useState(false);
  const [showPlanSwitchModal, setShowPlanSwitchModal] = useState(false);
  const [showAddPaymentModal, setShowAddPaymentModal] = useState(false);
  const [showManagePaymentModal, setShowManagePaymentModal] = useState(false);
  const [showCancelModal, setShowCancelModal] = useState(false);
  const [selectedNewPlan, setSelectedNewPlan] = useState<string | null>(null);
  const [paymentMethod, setPaymentMethod] = useState<PaymentMethod | null>(null);
  const [paymentMethods, setPaymentMethods] = useState<PaymentMethod[] | null>(null);
  const [schedules, setSchedules] = useState<Record<string, any>[] | null>(null);
  const [currentPlan, setCurrentPlan] = useState<Record<string, any>>(SUBSCRIPTION_PLANS[0]);
  const [activeTab, setActiveTab] = useState(0);
  const [editing, setEditing] = useState(false);
  const [oldProfileData, setOldProfileData] = useState<Record<string, any>>({});
  const [oldNotificationSettings, setOldNotificationSettings] = useState({
    emailNotifications: false,
    smsNotifications: false,
    shiftReminders: false,
    shiftChanges: false,
    shiftSwapRequests: false,
    timeOffApprovals: false,
    weeklySchedule: false,
    submitScheduleNotification: false,
    approvalsNotification: false,
    passwordChange: false
  });
  const [profileData, setProfileData] = useState<Record<string, any>>({});
  const [subscription, setSubscription] = useState<Record<string, any>>({});
  const [transactions, setTransactions] = useState<Transaction[]>([]);
  const [menuAnchor, setMenuAnchor] = useState<null | HTMLElement>(null);
  const [showUpgradePlan, setShowUpgradePlan] = useState(false);
  const [paymentMethodLoading, setPaymentMethodLoading] = useState(false);
  const [transactionsLoading, setTransactionsLoading] = useState(false);
  const [transactionsToShow, setTransactionsToShow] = useState(10);
  const [notificationSettings, setNotificationSettings] = useState({
    emailNotifications: false,
    smsNotifications: false,
    shiftReminders: false,
    shiftChanges: false,
    shiftSwapRequests: false,
    timeOffApprovals: false,
    weeklySchedule: false,
    submitScheduleNotification: false,
    approvalsNotification: false,
    passwordChange: false
  });
  const [securityData, setSecurityData] = useState({
    currentPassword: '',
    newPassword: '',
    confirmPassword: ''
  });
  const [isOwner, setIsOwner] = useState(false);

  // Load user data on component mount
  React.useEffect(() => {
    getUser(getUserPool()!.getCurrentUser()!.getUsername()).then((response) => {
      setOldProfileData(response[0]);
      setProfileData(response[0])
      setIsOwner(response[0].accountType === "owner")
      return response;
    }).then((response) => {
      if (response[0].accountType === "owner") {
        getSchedules(getUserPool()!.getCurrentUser()!.getUsername()).then((scheduleResponse) => {
          setSchedules(scheduleResponse);
        });
        setPaymentMethodLoading(true)
        setTransactionsLoading(true);
        getUserPaymentMethod(getUserPool()!.getCurrentUser()!.getUsername()).then((response) => {
          if (response.statusCode === 200) {
            JSON.parse(response.body).subscriptions.map((subscription: Record<string, any>) => {
              if (subscription.status === 'Active' || subscription.status === 'Pending') {
                setSubscription(subscription);
              }
            })
            const planId = JSON.parse(response.body).activePlanId;
            setCurrentPlan(SUBSCRIPTION_PLANS.find(plan => plan.planId === planId) || SUBSCRIPTION_PLANS[0]);
            setPaymentMethod(JSON.parse(response.body));
          }
          setPaymentMethodLoading(false);
          getTransactions(getUserPool()!.getCurrentUser()!.getUsername(), transactionsToShow).then((response) => {
            if (response.statusCode === 200) {
              setTransactions(JSON.parse(response.body).transactions);
            }
            setTransactionsLoading(false);
          })
          getCustomer(getUserPool()!.getCurrentUser()!.getUsername()).then((response) => {
            if (response.statusCode === 200) {
              setPaymentMethods(JSON.parse(response.body).paymentMethods);
            }
          })
        })
      }
    });
    getUserNotificationSettings(getUserPool()!.getCurrentUser()!.getUsername()).then((response) => {
      setNotificationSettings(response[0]);
      setOldNotificationSettings(response[0]);
    })
  }, []);

  React.useEffect(() => {
    if (isOwner) {
      setTransactionsLoading(true);
      getTransactions(getUserPool()!.getCurrentUser()!.getUsername(), transactionsToShow).then((response) => {
        if (response.statusCode === 200) {
          setTransactions(JSON.parse(response.body).transactions);
        }
        setTransactionsLoading(false);
      })
    }
  }, [isOwner, transactionsToShow])

  // Handler functions
  const handleProfileChange = (field: string, value: any) => {
    if (field === '_reset') {
      setProfileData(JSON.parse(JSON.stringify(value)));
      return;
    }
    setProfileData({ ...profileData, [field]: value });
  };

  const handleNotificationChange = (field: string) => {
    if (field === 'smsNotifications' && !notificationSettings.smsNotifications) {
      setShowSmsConsentModal(true);
      return;
    }

    setNotificationSettings({
      ...notificationSettings,
      [field]: !notificationSettings[field as keyof typeof notificationSettings]
    });
  };

  const validateFields = async () => {
    try {
      const { name, email, phone } = profileData || {};

      const validateName = (n: any) =>
        typeof n === 'string' && /^[A-Za-zÀ-ÖØ-öø-ÿ' -]{2,80}$/.test(n.trim());

      const validateEmail = (e: any) =>
        typeof e === 'string' && /^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(e.trim())

      const validatePhone = (p: any) => {
        if (typeof p !== 'string') return false;
        const trimmed = p.trim().replaceAll("-", "").replaceAll(" ", "").replaceAll("(", "").replaceAll(")", "");
        const tenDigits = /\+1\d{10}$/;
        return (tenDigits.test(trimmed));
      };

      if (!validateName(name)) {
        throw new Error(
          'Name is required and must be 2-80 characters. Only letters, spaces, hyphens and apostrophes are allowed.'
        );
      }

      if (email && !validateEmail(email)) {
        throw new Error('Please enter a valid email address.');
      }

      if (phone && !validatePhone("+1"+phone)) {
        throw new Error(
          'Please enter a valid phone number (E.164 format like +1234567890 preferred, or 10 digits).'
        );
      }

      return true;
    } catch (error) {
      console.error('Error validating profile:', error);
      throw error;
    }
  };

  const handleSaveProfile = async () => {
    try {
      try {
        await validateFields();
        if (profileData.email !== oldProfileData.email) {
          await updateUserAttribute('email', profileData.email);
          setAuth({ ...getAuth(), email_verified: 'false' });
        } else if (profileData.phone !== oldProfileData.phone) {
          await updateUserAttribute('phone_number', "+1"+profileData.phone);
          setAuth({ ...getAuth(), phone_number_verified: 'false' });
        }
        await (putUser(profileData.userId, profileData.accountType, profileData.name, profileData.email,
          "+1"+profileData.phone, profileData.created, profileData.archived, profileData.primarySchedule));
        profileData.phone = "+1"+profileData.phone
        setOldProfileData(profileData);
      }
      catch (validationError) {
        toast.showError((validationError as Error).message, 'Error');
        throw validationError;
      }

      setEditing(false);
      console.info(getAuth());
      setAuth({ ...getAuth(), name: profileData.name, email: profileData.email, phone: "+1"+profileData.phone } as any);
      console.info(getAuth());
    } catch (error) {
      console.error('Error updating profile:', error);
    }
  };

  const handleSaveNotifications = async () => {
    try {
      await putUserNotificationSettings(profileData.userId, notificationSettings.smsNotifications, notificationSettings.emailNotifications,
        notificationSettings.shiftChanges, notificationSettings.shiftReminders, notificationSettings.submitScheduleNotification, 
        notificationSettings.approvalsNotification, notificationSettings.passwordChange);
      setOldNotificationSettings(notificationSettings);
      toast.showSuccess('Notification settings updated successfully', 'success');
    } catch (error) {
      console.error('Error updating notifications:', error);
    }
  };

  const handleChangePassword = async () => {
    // Validate passwords match
    if (securityData.newPassword !== securityData.confirmPassword) {
      toast.showError('New passwords do not match. Please try again.', 'Validation Error');
      return;
    }

    // Validate password requirements
    const passwordRules = {
      length: securityData.newPassword.length >= 8,
      uppercase: /[A-Z]/.test(securityData.newPassword),
      lowercase: /[a-z]/.test(securityData.newPassword),
      number: /[0-9]/.test(securityData.newPassword),
      special: /[^A-Za-z0-9]/.test(securityData.newPassword),
    };

    const allRequirementsMet = Object.values(passwordRules).every(Boolean);
    if (!allRequirementsMet) {
      toast.showError('Password does not meet all requirements. Please check the requirements below.', 'Invalid Password');
      return;
    }

    try {
      await changePassword(securityData.currentPassword, securityData.newPassword);
      toast.showSuccess('Your password has been updated successfully.', 'Success');
      await putNotificationRequest(profileData.userId, '', 'passwordChange');
      setSecurityData({ currentPassword: '', newPassword: '', confirmPassword: '' });
    } catch (error: any) {
      const errorMessage = error?.message?.includes('Incorrect') 
        ? 'Current password is incorrect. Please try again.'
        : error?.message || 'Failed to update password. Please try again.';
      toast.showError(errorMessage, 'Password Update Failed');
    }
  };

  const handleDeleteAccount = async () => {
    try {
      if (subscription.id !== null) {
        toast.showError('Please cancel your subscription before deleting your account.');
        return;
      } else {
        await putUser(profileData.userId, profileData.accountType, profileData.name,
          profileData.email, profileData.phone, profileData.created, moment().format("YYYY-MM-DD"), profileData.primarySchedule);
        let archivedDate = moment().format("YYYY-MM-DD");
        setProfileData({ ...profileData, archived: archivedDate });
        setShowDeleteModal(false);
        logout();
      }
    } catch (error) {
      console.error('Error deleting account:', error);
    }
  };

  const handleSmsConsentAccept = () => {
    setNotificationSettings({
      ...notificationSettings,
      smsNotifications: true
    });
    setShowSmsConsentModal(false);
  };

  const handlePlanChange = async (planId: string) => {
    try {
      let response = null;
      if (subscription.id === null) {
        response = await putSubscription(profileData.userId, paymentMethod!.token, planId).then((response) => {
          setCurrentPlan(SUBSCRIPTION_PLANS.find(plan => plan.planId === planId) || SUBSCRIPTION_PLANS[0]);
          return response
        })
      } else {
        response = await updateSubscription(profileData.userId, paymentMethod!.token, subscription.id, planId).then((response) => {
          setCurrentPlan(SUBSCRIPTION_PLANS.find(plan => plan.planId === planId) || SUBSCRIPTION_PLANS[0]);
          return response
        })
      }
      if (response && response.statusCode === 200) {
        toast.showSuccess('Plan changed successfully', 'success');
      } else {
        toast.showError('Error changing plan', 'error');
      }
    } catch (error) {
      console.error('Error changing plan:', error);
    }

    setShowPlanSwitchModal(false);
    setSelectedNewPlan(null);
  };

  const handlePaymentMethodAdded = (selectedPayment?: PaymentMethod | null) => {
    if (selectedPayment) {
      setPaymentMethod(selectedPayment);
      updateSubscription(profileData.userId, selectedPayment.token,
        subscription.id, currentPlan.planId).then((response) => {
          console.info(response)
        });
    } else {
      getUserPaymentMethod(getUserPool()!.getCurrentUser()!.getUsername()).then((response) => {
        if (response.statusCode === 200) {
          JSON.parse(response.body).subscriptions.map((subscription: Record<string, any>) => {
            if (subscription.status === 'Active' || subscription.status === 'Pending') {
              setSubscription(subscription);
            }
          })
          setPaymentMethod(JSON.parse(response.body));
          setPaymentMethodLoading(false);
        }
      });
      getCustomer(getUserPool()!.getCurrentUser()!.getUsername()).then((response) => {
        if (response.statusCode === 200) {
          setPaymentMethods(JSON.parse(response.body).paymentMethods);
        }
      });
      toast.showSuccess('Payment method added successfully!', 'success');
    }
  };

  const handleVerifyAttribute = async (attributeName: 'email' | 'phone_number', code: string) => {
    try {
      await verifyUserAttribute(attributeName, code);
      toast.showSuccess(`${attributeName === 'email' ? 'Email' : 'Phone'} verified successfully!`, 'success');
    } catch (error) {
      console.error('Error verifying attribute:', error);
      toast.showError(`Failed to verify ${attributeName === 'email' ? 'email' : 'phone number'}`, 'error');
      throw error;
    }
  };

  const handleProfileCancel = () => {
    setEditing(false);
    setProfileData(JSON.parse(JSON.stringify(oldProfileData)));
  };

  return (
    <Box sx={{ minHeight: '100vh', bgcolor: 'grey.50' }}>

      {/* Header */}
      <MainHeader title={`Profile & Settings`}
        subTitle={`Profile - ${getAuth().name || ""}`}
        onClickLogo={() => navigate('/')}
        onClickLogout={() => { logout(); navigate('/login'); }}
        onBack={() => navigate('/dashboard')}
      />

      <Container maxWidth="lg" sx={{ py: 4 }}>
        <Grid container spacing={3}>

          {/* Sidebar */}
          <Grid item xs={12} md={3}>
            <Paper sx={{ p: 3, textAlign: 'center', position: 'sticky', top: 80 }}>
              <Box sx={{ position: 'relative', display: 'inline-block', mb: 2 }}>
                <Avatar
                  sx={{
                    width: 100,
                    height: 100,
                    bgcolor: 'primary.main',
                    fontSize: '2.5rem',
                    margin: '0 auto'
                  }}
                >
                  {oldProfileData.name?.split(' ').map((n: any[]) => n[0]).join('')}
                </Avatar>
                {editing && (
                  <IconButton
                    sx={{
                      position: 'absolute',
                      bottom: 0,
                      right: 0,
                      bgcolor: 'primary.main',
                      color: 'white',
                      '&:hover': { bgcolor: 'primary.dark' }
                    }}
                    size="small"
                  >
                    <PhotoCameraIcon fontSize="small" />
                  </IconButton>
                )}
              </Box>
              <Typography variant="h6" fontWeight="bold" gutterBottom>
                {oldProfileData.name}
              </Typography>
              {isOwner ? <Chip
                label="Business Account"
                size="small"
                color="primary"
                sx={{ mb: 2 }}
              /> : <></>}
              <Typography variant="body2" color="text.secondary" sx={{ mb: 1 }}>
                {oldProfileData.email}
              </Typography>
              <Typography variant="caption" color="text.secondary">
                Member since {moment(oldProfileData.created).format('MMM YYYY')}
              </Typography>
            </Paper>
          </Grid>

          {/* Main Content */}
          <Grid item xs={12} md={9}>
            <Paper sx={{ mb: 3 }}>
              <Tabs
                value={activeTab}
                onChange={(e, val) => setActiveTab(val)}
                sx={{ borderBottom: 1, borderColor: 'divider' }}
                variant="scrollable"
                scrollButtons="auto"
                allowScrollButtonsMobile
              >
                <Tab label="Profile" icon={<PersonIcon />} iconPosition="start" />
                <Tab label="Notifications" icon={<NotificationsIcon />} iconPosition="start" />
                <Tab label="Security" icon={<SecurityIcon />} iconPosition="start" />
                {isOwner && <Tab label="Billing" icon={<CreditCardIcon />} iconPosition="start" />}
              </Tabs>
            </Paper>

            {/* Profile Tab */}
            {activeTab === 0 && (
              <ProfileTab
                editing={editing}
                profileData={profileData}
                oldProfileData={oldProfileData}
                schedules={schedules}
                isOwner={isOwner}
                onProfileChange={handleProfileChange}
                onEditToggle={setEditing}
                onSave={handleSaveProfile}
                onVerifyAttribute={handleVerifyAttribute}
                onCancel={handleProfileCancel}
              />
            )}

            {/* Notifications Tab */}
            {activeTab === 1 && (
              <NotificationsTab
                notificationSettings={notificationSettings}
                oldNotificationSettings={oldNotificationSettings}
                profileData={profileData}
                userData={profileData}
                onNotificationChange={handleNotificationChange}
                onSave={handleSaveNotifications}
              />
            )}

            {/* Security Tab */}
            {activeTab === 2 && (
              <SecurityTab
                profileData={profileData}
                securityData={securityData}
                isOwner={isOwner}
                onSecurityDataChange={setSecurityData}
                onChangePassword={handleChangePassword}
                onDeleteAccount={() => setShowDeleteModal(true)}
                onCancelDeletion={() => {
                  putUser(profileData.userId, profileData.accountType, profileData.name,
                    profileData.email, profileData.phone, profileData.created, undefined, profileData.primarySchedule).then(() => {
                      setProfileData({ ...profileData, archived: null });
                    })
                }}
              />
            )}

            {/* Billing Tab (Owner Only) */}
            {activeTab === 3 && isOwner && (
              <BillingTab
                currentPlan={currentPlan}
                paymentMethod={paymentMethod}
                subscription={subscription}
                transactions={transactions}
                transactionsToShow={transactionsToShow}
                paymentMethodLoading={paymentMethodLoading}
                transactionsLoading={transactionsLoading}
                profileData={profileData}
                selectedNewPlan={selectedNewPlan}
                showUpgradePlan={showUpgradePlan}
                plans={SUBSCRIPTION_PLANS}
                onUpgradePlanClick={() => setShowUpgradePlan(!showUpgradePlan)}
                onPaymentMethodClick={() => {
                  if (!paymentMethod) {
                    setShowAddPaymentModal(true);
                  } else {
                    setShowManagePaymentModal(true);
                  }
                }}
                setShowPlanSwitchModal={setShowPlanSwitchModal}
                setSelectedNewPlan={setSelectedNewPlan}
                onTransactionsShowChange={setTransactionsToShow}
                onMenuClick={(e) => setMenuAnchor(e.currentTarget)}
              />
            )}
          </Grid>
        </Grid>
      </Container>

      {/* Subscription Management Menu */}
      <Menu
        anchorEl={menuAnchor}
        open={Boolean(menuAnchor)}
        onClose={() => setMenuAnchor(null)}
        anchorOrigin={{
          vertical: 'bottom',
          horizontal: 'right',
        }}
        transformOrigin={{
          vertical: 'top',
          horizontal: 'right',
        }}
      >
        <MenuItem
          onClick={() => {
            setMenuAnchor(null);
            setShowCancelModal(true);
          }}
          sx={{ color: 'error.main' }}
        >
          <ListItemIcon>
            <CancelIcon fontSize="small" color="error" />
          </ListItemIcon>
          <ListItemText
            primary="Cancel Subscription"
            secondary="End your subscription"
          />
        </MenuItem>
      </Menu>

      {/* Modal Components */}
      <AddPaymentModal
        open={showAddPaymentModal}
        onClose={() => setShowAddPaymentModal(false)}
        userData={profileData}
        onSuccess={handlePaymentMethodAdded}
        paymentMethodLoading={paymentMethodLoading}
        setPaymentMethodLoading={setPaymentMethodLoading}
        subscriptionId={subscription?.id}
        planId={currentPlan?.planId}
      />

      <ManagePaymentMethodModal
        open={showManagePaymentModal}
        onClose={() => setShowManagePaymentModal(false)}
        onDelete={(payment) => {
          try {
            deletePaymentMethod(profileData.userId, payment!.token).then(() => {
              setPaymentMethods(paymentMethods?.filter(pm => pm.globalId !== payment!.globalId) || []);
              toast.showSuccess('Payment method deleted successfully!', 'success');
            });
          } catch (error) {
            toast.showError(`Failed to delete payment method`);
          }
        }}
        userData={profileData}
        onFinish={handlePaymentMethodAdded}
        paymentMethods={paymentMethods || []}
        currentPaymentMethod={paymentMethod}
        planId={currentPlan?.planId}
        subscriptionId={subscription?.id}
      />

      <SmsConsentModal
        open={showSmsConsentModal}
        onClose={() => setShowSmsConsentModal(false)}
        onAccept={handleSmsConsentAccept}
        phoneNumber={profileData.phone}
      />

      <PlanSwitchModal
        open={showPlanSwitchModal}
        onClose={() => setShowPlanSwitchModal(false)}
        paymentMethod={paymentMethod}
        currentPlan={currentPlan}
        newPlan={SUBSCRIPTION_PLANS.find(plan => plan.planId === selectedNewPlan) || SUBSCRIPTION_PLANS[0]}
        onConfirm={() => {
          handlePlanChange(selectedNewPlan!);
          setShowPlanSwitchModal(false);
        }}
      />

      <CancelSubscriptionModal
        open={showCancelModal}
        onClose={() => setShowCancelModal(false)}
        subscription={subscription}
        currentPlan={currentPlan}
        onConfirm={() => {
          cancelSubscription(profileData.userId, subscription.id);
          setShowCancelModal(false);
        }}
      />

      <DeleteAccountModal
        open={showDeleteModal}
        onClose={() => setShowDeleteModal(false)}
        isOwner={isOwner}
        onConfirm={handleDeleteAccount}
      />
    </Box >
  );
}